// src/admin/pages/TenantManagementPage.jsx
//
// v206: lists retired tenants too («— ritirato»), shows the id (monospace + «Copia»), «Ritira»/«Riattiva»
// (ConfirmDialog → OutcomeDialog from the response) and «Elimina definitivamente…» (greyed while active; preview +
// typed id in TypedConfirmDialog). ⛔ After a purge the selection is cleared BEFORE the re-read and the console's
// stored "last tenant" keys are forgotten, so nothing names the purged id again (it would answer 410 and Plan A
// would show its "purged" banner). ⛔ Errors are worded from err.code (admin/adminErrorCopy.js), never the error's server text.
// v212: `?tenant=<id>` preselects a tenant (links from the «Aziende» page); «Apri azienda» for a tenant with a
// company; a purge outcome names the companies it leaves with no tenant, its action opens the first.
// v215: under «Utenti di …», a note that the owning company's admins reach the tenant too (no membership row).
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Copy } from 'lucide-react';
import AdminTable from '../components/AdminTable.jsx';
import ConfirmDialog from '../components/ConfirmDialog.jsx';
import TenantCreditsSection from './TenantCreditsSection.jsx';
import OutcomeDialog from '../../components/ui/OutcomeDialog.jsx';
import TypedConfirmDialog from '../purge/TypedConfirmDialog.jsx';
import { TenantPurgeSummary } from '../purge/PurgeSummaries.jsx';
import { usePurgePreview } from '../purge/usePurgePreview.js';
import { firstBlocker, isIncomplete, resultCounts } from '../purge/purgeModel.js';
import { adminErrorText, isGoneError } from '../adminErrorCopy.js';
import { clearAdminLastTenantKeys } from '../../auth/tenantPurged.js';
import { companyPath, orphanCompanies, orphanLineKeys } from '../companies/companyModel.js';
import {
  fetchUsers,
  fetchApplications,
  assignAppToTenant,
  removeUserFromTenant,
  adminListTenants,
  adminListTenantApps,
  adminDeactivateTenant,
  adminActivateTenant,
  previewTenantPurge,
  purgeTenant,
} from '../adminApi.js';
import { API_BASE_URL } from '../../services/api/config.js';
import { COLORS, FONT_MONO, FONT_SANS, RADII, SEMANTIC } from '../../constants/theme.js';

const cardStyle = {
  background: COLORS.surface, borderRadius: RADII.control,
  border: `1px solid ${COLORS.border}`, padding: '24px', marginBottom: '24px',
};

const btnStyle = (color = COLORS.ink) => ({
  display: 'inline-flex', alignItems: 'center', gap: '6px',
  padding: '6px 12px', borderRadius: RADII.controlInner,
  border: `1px solid ${COLORS.borderStrong}`, background: COLORS.surface,
  color, fontSize: '12px', cursor: 'pointer',
  fontFamily: FONT_SANS,
});

export default function TenantManagementPage() {
  const { t } = useTranslation('admin');
  const [tenants, setTenants] = useState([]);
  const [tenantsError, setTenantsError] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Read once: a link from the «Aziende» page lands with its tenant selected; the <select> owns it afterwards.
  const [selectedTenant, setSelectedTenant] = useState(() => searchParams.get('tenant') || '');
  const [users, setUsers] = useState([]);
  const [allApps, setAllApps] = useState([]);
  const [assignedApps, setAssignedApps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [outcome, setOutcome] = useState(null);
  const [lifecycleConfirm, setLifecycleConfirm] = useState(false);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const purgeGoneRef = useRef(false);

  const loadTenants = useCallback(async () => {
    try {
      setTenants(await adminListTenants({ includeInactive: true }));
      setTenantsError('');
    } catch (err) {
      setTenantsError(adminErrorText(t, err));
    }
  }, [t]);

  useEffect(() => { loadTenants(); }, [loadTenants]);

  const selectedRow = tenants.find(tn => tn.id === selectedTenant) || null;
  // null/unknown is_active fails open (CLAUDE.md): only a known `true` greys the purge.
  const isActive = selectedRow?.is_active === true;
  const isRetired = selectedRow?.is_active === false;
  const preview = usePurgePreview(purgeOpen, () => previewTenantPurge(selectedTenant));

  useEffect(() => {
    fetchApplications().then(setAllApps).catch(() => {});
  }, []);

  const loadTenantUsers = useCallback(async () => {
    if (!selectedTenant) { setUsers([]); return; }
    setLoading(true);
    try {
      const data = await fetchUsers({ limit: 200, tenant: selectedTenant });
      setUsers(data.users);
    } catch { setUsers([]); }
    finally { setLoading(false); }
  }, [selectedTenant]);

  useEffect(() => { loadTenantUsers(); }, [loadTenantUsers]);

  useEffect(() => {
    if (!selectedTenant) { setAssignedApps([]); return; }
    adminListTenantApps(selectedTenant).then(setAssignedApps).catch(() => setAssignedApps([]));
  }, [selectedTenant]);

  // Invoked as ConfirmDialog.onConfirm — the dialog awaits this and shows a rejection's message inline,
  // so the rejection carries OUR sentence.
  async function handleRemoveUser(userId) {
    try {
      await removeUserFromTenant(selectedTenant, userId);
    } catch (err) {
      throw new Error(adminErrorText(t, err));
    }
    loadTenantUsers();
  }

  async function handleAssignApp(appId) {
    try {
      await assignAppToTenant(selectedTenant, appId);
      alert(t('tenants.appAssigned'));
    } catch (err) { alert(adminErrorText(t, err)); }
  }

  async function handleLifecycle() {
    const name = selectedRow?.display_name || selectedTenant;
    let row;
    try {
      // Keyed on isRetired like the button: an unknown is_active reads «Ritira» and retires (the server decides).
      row = isRetired ? await adminActivateTenant(selectedTenant) : await adminDeactivateTenant(selectedTenant);
    } catch (err) {
      throw new Error(adminErrorText(t, err));
    }
    await loadTenants();
    // Worded from the response, not from which button was pressed (both calls are idempotent).
    setOutcome({
      tone: 'success',
      text: row?.is_active === false
        ? t('purge.tenant.outcomeRetired', { name })
        : t('purge.tenant.outcomeReactivated', { name }),
    });
  }

  async function handlePurge(typed) {
    const id = selectedTenant;
    const name = selectedRow?.display_name || id;
    let result;
    try {
      result = await purgeTenant(id, typed);
    } catch (err) {
      if (isGoneError(err)) purgeGoneRef.current = true;
      throw err; // TypedConfirmDialog words it
    }
    // D9 — order matters: nothing may name the purged id again.
    setPurgeOpen(false);
    setSelectedTenant('');
    clearAdminLastTenantKeys(id);
    await loadTenants();
    if (isIncomplete(result)) {
      setOutcome({ tone: 'warning', text: t('purge.incomplete') });
      return;
    }
    const { deleted, anonymised } = resultCounts(result);
    const accounts = deleted + anonymised > 0 ? ` ${t('purge.tenant.outcomeAccounts', { deleted, anonymised })}` : '';
    const orphans = orphanCompanies(result);
    const orphanText = orphanLineKeys(orphans).map((l) => ` ${t(l.key, l.params)}`).join('');
    setOutcome({
      tone: 'success',
      text: `${t('purge.tenant.outcome', { name })}${accounts}${orphanText}`,
      ...(orphans.length > 0
        ? { action: { label: t('purge.orphan.open'), onClick: () => navigate(companyPath(orphans[0].customer_id)) } }
        : {}),
    });
  }

  function closePurge() {
    setPurgeOpen(false);
    // Already gone (purged elsewhere): drop the selection and re-read.
    if (purgeGoneRef.current || isGoneError(preview.error)) {
      purgeGoneRef.current = false;
      setSelectedTenant('');
      loadTenants();
    }
  }

  function copyId() {
    const clipboard = typeof navigator !== 'undefined' ? navigator.clipboard : undefined;
    // Clipboard unavailable or refused: the id stays visible to select by hand.
    if (!clipboard?.writeText) return;
    clipboard.writeText(selectedTenant).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }, () => {});
  }

  const blockedText = preview.error
    ? adminErrorText(t, preview.error, { purge: true })
    : (firstBlocker(preview.preview) ? adminErrorText(t, { code: firstBlocker(preview.preview) }) : null);

  const userColumns = [
    { key: 'email', label: t('shared.email') },
    {
      key: 'is_active', label: t('shared.status'), width: '80px',
      render: (row) => (
        <span style={{ color: row.is_active ? SEMANTIC.verde.fg : SEMANTIC.errore.fg, fontSize: '12px' }}>
          {row.is_active ? t('shared.active') : t('shared.inactive')}
        </span>
      ),
    },
    {
      key: 'actions', label: '', width: '100px',
      render: (row) => (
        <button
          onClick={() => setConfirmAction({
            title: t('shared.removeFromTenantTitle'),
            message: t('tenants.removeMessage', { email: row.email }),
            onConfirm: () => handleRemoveUser(row.user_id),
            variant: 'danger',
          })}
          style={btnStyle(SEMANTIC.errore.fg)}
        >
          {t('shared.remove')}
        </button>
      ),
    },
  ];

  const selectedTenantName = selectedRow?.display_name || '';

  return (
    <div style={{ padding: '32px 40px', fontFamily: FONT_SANS, color: COLORS.ink, maxWidth: '1100px' }}>
      <h1 style={{ fontSize: '22px', fontWeight: 700, marginBottom: '4px' }}>{t('tenants.title')}</h1>
      <p style={{ color: COLORS.textSecondary, fontSize: '13px', marginBottom: '24px' }}>
        {t('tenants.subtitle')}
      </p>

      {tenantsError && (
        <div style={{ marginBottom: '16px', padding: '12px 16px', borderRadius: RADII.controlInner, background: SEMANTIC.errore.bg, border: `1px solid ${SEMANTIC.errore.bg}`, color: SEMANTIC.errore.fg, fontSize: '13px' }}>
          {tenantsError}
        </div>
      )}

      <div style={{ marginBottom: '24px' }}>
        <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '13px', marginBottom: '6px', fontWeight: 500 }}>
          {t('tenants.selectTenant')}
        </label>
        <select
          value={selectedTenant}
          onChange={e => setSelectedTenant(e.target.value)}
          style={{
            padding: '10px 14px', borderRadius: RADII.controlInner, border: `1px solid ${COLORS.borderStrong}`,
            fontSize: '14px', fontFamily: FONT_SANS, minWidth: '300px',
          }}
        >
          <option value="">{t('shared.selectPlaceholder')}</option>
          {tenants.map(tn => (
            <option key={tn.id} value={tn.id}>
              {tn.is_active === false ? t('purge.tenant.retiredOption', { name: tn.display_name }) : tn.display_name}
            </option>
          ))}
        </select>
      </div>

      {selectedTenant && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
            <img
              src={`${API_BASE_URL}/api/v1/tenants/${selectedTenant}/logo`}
              alt={selectedTenantName}
              style={{ height: '36px', width: 'auto' }}
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
            <span style={{ fontSize: '15px', fontWeight: 600, color: COLORS.ink }}>{selectedTenantName}</span>
            <span style={{ fontSize: '12px', color: COLORS.textSecondary }}>
              {t('purge.tenant.idLabel')} <code style={{ fontFamily: FONT_MONO, fontSize: '12px' }}>{selectedTenant}</code>
            </span>
            <button type="button" onClick={copyId} style={btnStyle()}>
              <Copy size={13} /> {copied ? t('shared.copied') : t('shared.copy')}
            </button>
            {/* Unknown status (no is_active): no chip, never a guessed «Attivo». */}
            {(isActive || isRetired) && (
              <span style={{
                padding: '2px 10px', borderRadius: RADII.controlInner, fontSize: '12px', fontWeight: 600,
                background: isRetired ? SEMANTIC.attenzione.bg : SEMANTIC.verde.bg,
                color: isRetired ? SEMANTIC.attenzione.fg : SEMANTIC.verde.fg,
              }}>
                {isRetired ? t('purge.tenant.retired') : t('purge.tenant.active')}
              </span>
            )}
            {selectedRow?.customer_id && (
              <button type="button" onClick={() => navigate(companyPath(selectedRow.customer_id))} style={btnStyle()}>
                {t('tenants.openCompany')}
              </button>
            )}
            <span style={{ flex: 1 }} />
            <button
              type="button"
              onClick={() => setLifecycleConfirm(true)}
              style={btnStyle(isRetired ? SEMANTIC.verde.fg : SEMANTIC.errore.fg)}
            >
              {isRetired ? t('purge.tenant.reactivate') : t('purge.tenant.retire')}
            </button>
            <button
              type="button"
              onClick={() => setPurgeOpen(true)}
              disabled={isActive}
              title={isActive ? t('purge.tenant.purgeDisabled') : undefined}
              style={{ ...btnStyle(SEMANTIC.errore.fg), opacity: isActive ? 0.5 : 1, cursor: isActive ? 'not-allowed' : 'pointer' }}
            >
              {t('purge.tenant.purge')}
            </button>
          </div>

          <TenantCreditsSection tenantId={selectedTenant} />

          <div style={cardStyle}>
            <h3 style={{ fontSize: '15px', fontWeight: 600, marginBottom: '16px' }}>
              {t('tenants.usersOf', { name: selectedTenantName })}
            </h3>
            <AdminTable columns={userColumns} rows={users} loading={loading} emptyMessage={t('tenants.noUsers')} />
            {/* v215: a company admin reaches this tenant with no membership row, so this list is not everyone. */}
            {selectedRow?.customer_id && (
              <p style={{ margin: '12px 0 0', fontSize: '12px', lineHeight: 1.5, color: COLORS.textSecondary }}>
                {t('tenants.companyAdminsNote')}
              </p>
            )}
          </div>

          <div style={cardStyle}>
            <h3 style={{ fontSize: '15px', fontWeight: 600, marginBottom: '16px' }}>{t('tenants.assignedApps')}</h3>
            {assignedApps.length === 0 ? (
              <p style={{ color: COLORS.textSecondary, fontSize: '13px' }}>{t('tenants.noAssignedApps')}</p>
            ) : (
              <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {assignedApps.map(app => (
                  <li key={app.id} style={{ fontSize: '13px', color: COLORS.ink }}>
                    <strong>{app.display_name}</strong>
                    {app.description ? <span style={{ color: COLORS.textSecondary }}> — {app.description}</span> : null}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div style={cardStyle}>
            <h3 style={{ fontSize: '15px', fontWeight: 600, marginBottom: '16px' }}>{t('tenants.apps')}</h3>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {allApps.map(app => (
                <button key={app.id} onClick={() => handleAssignApp(app.id)} style={btnStyle(COLORS.accent)}>
                  {app.display_name}
                </button>
              ))}
            </div>
            {allApps.length === 0 && (
              <p style={{ color: COLORS.textSecondary, fontSize: '13px' }}>{t('tenants.noApps')}</p>
            )}
          </div>
        </>
      )}

      {confirmAction && (
        <ConfirmDialog
          open={true}
          onClose={() => setConfirmAction(null)}
          onConfirm={confirmAction.onConfirm}
          title={confirmAction.title}
          message={confirmAction.message}
          variant={confirmAction.variant}
        />
      )}

      {lifecycleConfirm && (
        <ConfirmDialog
          open
          onClose={() => setLifecycleConfirm(false)}
          onConfirm={handleLifecycle}
          title={isRetired ? t('purge.tenant.reactivateTitle') : t('purge.tenant.retireTitle')}
          message={isRetired
            ? t('purge.tenant.reactivateMessage', { name: selectedTenantName })
            : t('purge.tenant.retireMessage', { name: selectedTenantName })}
          confirmLabel={isRetired ? t('purge.tenant.reactivate') : t('purge.tenant.retire')}
          variant={isRetired ? 'default' : 'danger'}
        />
      )}
      <TypedConfirmDialog
        open={purgeOpen}
        title={t('purge.tenant.purgeTitle', { name: selectedTenantName })}
        expected={selectedTenant}
        inputLabel={t('purge.tenant.typeLabel', { id: selectedTenant })}
        confirmLabel={t('purge.tenant.confirm')}
        loading={preview.loading}
        onRetry={preview.reload}
        blockedText={blockedText}
        onConfirm={handlePurge}
        onClose={closePurge}
      >
        {preview.preview && <TenantPurgeSummary preview={preview.preview} />}
      </TypedConfirmDialog>
      <OutcomeDialog outcome={outcome} onClose={() => setOutcome(null)} />
    </div>
  );
}
